import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import workPolicyApplicationApi, {
  WorkPolicyApplicationApiError
} from "../../../../api/work-policy-application-api";
import type {
  ApprovalStaggeredTime,
  EligibleWorkPolicy,
  WorkPolicyApplicationApi
} from "../../../../api/work-policy-application-api.types";
import { addCalendarDays, parseCalendarDate } from "./calendar-date";
import {
  clampCalendarRangeEnd,
  MAX_FLEXIBLE_DATE_RANGE_MONTHS
} from "../../../work-type-shared/calendar-date-range";
import type { EligiblePolicyStatus } from "./eligible-policy-select";
import { requiresWorkPolicySelectionConfirmation } from "./work-policy-application.reducer";
import { hydrateWorkPolicyApplicationDetail } from "./work-policy-application.serialization";
import type {
  WorkPolicyApplicationDraft,
  WorkPolicyApplicationPolicySnapshot,
  WorkPolicyApplicationScheduleRow
} from "./work-policy-application.types";
import type { WorkPolicyApplicationController } from "./use-work-policy-application-controller";

export interface WorkPolicyApplicationDateField {
  value: string;
  onChange: (value: string) => void;
}

interface UseWorkPolicyApplicationFormModelOptions {
  startDate: WorkPolicyApplicationDateField;
  endDate: WorkPolicyApplicationDateField;
  controller: WorkPolicyApplicationController;
  api?: WorkPolicyApplicationApi;
}

export interface WorkPolicyApplicationFormModel {
  controller: WorkPolicyApplicationController;
  eligibleStatus: EligiblePolicyStatus;
  policies: EligibleWorkPolicy[];
  selectedPolicy?: EligibleWorkPolicy;
  staggeredStatus: "loading" | "ready" | "error";
  staggeredOptions: ApprovalStaggeredTime[];
  selectPolicy: (policy: EligibleWorkPolicy) => void;
  updateDate: (field: "startDate" | "endDate", value: string) => void;
  updateStaggeredStartTime: (selectedStartTime: string) => void;
  updateFlexibleSchedule: (value: WorkPolicyApplicationScheduleRow[]) => void;
  retryEligible: () => void;
  retryStaggered: () => void;
  retrySettlement: () => void;
}

function validSettlementRange(startDate: string, endDate: string) {
  try {
    parseCalendarDate(startDate);
    parseCalendarDate(endDate);
    return startDate <= endDate;
  } catch {
    return false;
  }
}

function settlementKey(startDate: string, endDate: string) {
  return `${startDate}:${endDate}`;
}

function snapshot(policy: EligibleWorkPolicy): WorkPolicyApplicationPolicySnapshot {
  return {
    profileName: policy.profileName,
    dailyBaseHours: policy.dailyBaseHours,
    weeklyBaseHours: policy.weeklyBaseHours,
    weeklyOvertimeLimit: policy.weeklyOvertimeLimit,
    breakCalculationEnabled: policy.breakCalculationEnabled,
    breakWorkMinutes: policy.breakWorkMinutes,
    breakMinutes: policy.breakMinutes,
    flexibleMode: policy.flexibleMode,
    selectiveCoreEnabled: policy.selectiveCoreEnabled,
    selectiveCoreStart: policy.selectiveCoreStart,
    selectiveCoreEnd: policy.selectiveCoreEnd,
    nightStart: policy.nightStart,
    nightEnd: policy.nightEnd,
    workPolicySnapshotJson: JSON.stringify(policy)
  };
}

export function createWorkPolicyApplicationSchedule(
  mode: "DAILY_DATE" | "WEEKLY_PATTERN",
  startDate: string,
  endDate: string
): WorkPolicyApplicationScheduleRow[] {
  if (mode === "WEEKLY_PATTERN") {
    return Array.from({ length: 7 }, (_, index) => ({
      workDate: null,
      weekdayNo: index + 1,
      startTime: index < 5 ? "09:00" : null,
      plannedWorkMinutes: index < 5 ? 480 : 0,
      breakWaiver: false,
      workday: index < 5
    }));
  }
  if (!startDate || !endDate || startDate > endDate) return [];
  const rows: WorkPolicyApplicationScheduleRow[] = [];
  let date = startDate;
  while (date <= endDate) {
    rows.push({
      workDate: date,
      weekdayNo: null,
      startTime: "09:00",
      plannedWorkMinutes: 480,
      breakWaiver: false,
      workday: true
    });
    date = addCalendarDays(date, 1);
  }
  return rows;
}

export function createWorkPolicyApplicationDraft(
  policy: EligibleWorkPolicy,
  startDate: string,
  endDate: string
): WorkPolicyApplicationDraft {
  const constrainedEndDate =
    policy.workType === "flexible" && policy.flexibleMode === "DAILY_DATE"
      ? clampCalendarRangeEnd(
          startDate,
          endDate,
          MAX_FLEXIBLE_DATE_RANGE_MONTHS
        )
      : endDate;
  const common = {
    workPolicyId: policy.workPolicyId,
    startDate,
    endDate: constrainedEndDate,
    policySnapshot: snapshot(policy)
  };
  if (policy.workType === "staggered") {
    return {
      ...common,
      workType: "staggered",
      allowedStartTimes: [],
      selectedStartTime: null
    };
  }
  if (policy.workType === "selective") {
    return {
      ...common,
      workType: "selective",
      settlementStatus: "loading",
      settlementPeriods: []
    };
  }
  return {
    ...common,
    workType: "flexible",
    flexibleMode: policy.flexibleMode,
    schedule: createWorkPolicyApplicationSchedule(
      policy.flexibleMode,
      startDate,
      constrainedEndDate
    )
  };
}

export function useWorkPolicyApplicationFormModel({
  startDate,
  endDate,
  controller,
  api = workPolicyApplicationApi
}: UseWorkPolicyApplicationFormModelOptions): WorkPolicyApplicationFormModel {
  const [eligibleStatus, setEligibleStatus] =
    useState<EligiblePolicyStatus>("loading");
  const [policies, setPolicies] = useState<EligibleWorkPolicy[]>([]);
  const [staggeredStatus, setStaggeredStatus] = useState<
    "loading" | "ready" | "error"
  >("ready");
  const [staggeredOptions, setStaggeredOptions] = useState<
    ApprovalStaggeredTime[]
  >([]);
  const hydratedRequestId = useRef<number>();
  const draftRef = useRef(controller.draft);
  const settlementRequestSequence = useRef(0);
  const requestedSettlementKey = useRef<string>();
  const resolvedSettlementKey = useRef<string>();
  const [settlementRetryNonce, setSettlementRetryNonce] = useState(0);
  draftRef.current = controller.draft;

  const loadEligible = useCallback(async () => {
    setEligibleStatus("loading");
    try {
      const response = await api.eligible();
      setPolicies(response.data);
      setEligibleStatus("ready");
    } catch (error) {
      const forbidden =
        error instanceof WorkPolicyApplicationApiError &&
        (error.status === 403 || error.code === "WORK_POLICY_FORBIDDEN");
      setEligibleStatus(forbidden ? "forbidden" : "error");
    }
  }, [api]);

  useEffect(() => {
    void loadEligible();
  }, [loadEligible]);

  const approvalRequestIdx = controller.approvalRequestIdx;
  const setDraft = controller.setDraft;
  const setUnsupportedHistorical = controller.setUnsupportedHistorical;
  const setStatus = controller.setStatus;
  const setRecovery = controller.setRecovery;
  const setIsDirty = controller.setIsDirty;
  const startDateOnChange = startDate.onChange;
  const endDateOnChange = endDate.onChange;

  useEffect(() => {
    if (!approvalRequestIdx || hydratedRequestId.current === approvalRequestIdx) {
      return;
    }
    hydratedRequestId.current = approvalRequestIdx;
    void api
      .detail(approvalRequestIdx)
      .then(response => {
        const hydration = hydrateWorkPolicyApplicationDetail(response.data);
        if (hydration.draft.workType === "selective") {
          const key = settlementKey(
            hydration.draft.startDate,
            hydration.draft.endDate
          );
          requestedSettlementKey.current = key;
          resolvedSettlementKey.current = key;
        }
        setUnsupportedHistorical(null);
        setDraft(hydration.draft);
        startDateOnChange(hydration.draft.startDate);
        endDateOnChange(hydration.draft.endDate);
        setStatus("draft");
        setIsDirty(false);
      })
      .catch(error => {
        setRecovery({
          code:
            error instanceof WorkPolicyApplicationApiError
              ? error.code
              : "WORK_POLICY_DETAIL_FAILED",
          message:
            error instanceof Error
              ? error.message
              : "임시저장 문서를 불러오지 못했습니다.",
          retryable: true,
          networkUnknown: false
        });
        setStatus("error");
      });
  }, [
    api,
    approvalRequestIdx,
    endDateOnChange,
    setDraft,
    setIsDirty,
    setRecovery,
    setStatus,
    setUnsupportedHistorical,
    startDateOnChange
  ]);

  const draft = controller.draft;

  useEffect(() => {
    if (draft?.workType !== "selective") {
      settlementRequestSequence.current += 1;
      requestedSettlementKey.current = undefined;
      resolvedSettlementKey.current = undefined;
      return;
    }
    if (!validSettlementRange(draft.startDate, draft.endDate)) {
      settlementRequestSequence.current += 1;
      requestedSettlementKey.current = undefined;
      resolvedSettlementKey.current = undefined;
      if (
        draft.settlementStatus !== "loading" ||
        draft.settlementPeriods.length > 0
      ) {
        const next = {
          ...draft,
          settlementStatus: "loading" as const,
          settlementPeriods: []
        };
        draftRef.current = next;
        setDraft(next);
      }
      return;
    }

    const key = settlementKey(draft.startDate, draft.endDate);
    if (
      requestedSettlementKey.current === key ||
      resolvedSettlementKey.current === key
    ) {
      return;
    }
    requestedSettlementKey.current = key;
    resolvedSettlementKey.current = undefined;
    const sequence = ++settlementRequestSequence.current;
    const loadingDraft = {
      ...draft,
      settlementStatus: "loading" as const,
      settlementPeriods: []
    };
    draftRef.current = loadingDraft;
    setDraft(loadingDraft);

    void api
      .settlementPeriods(draft.startDate, draft.endDate)
      .then(response => {
        if (sequence !== settlementRequestSequence.current) return;
        const latest = draftRef.current;
        if (
          latest?.workType !== "selective" ||
          settlementKey(latest.startDate, latest.endDate) !== key
        ) {
          return;
        }
        const readyDraft = {
          ...latest,
          settlementStatus: "ready" as const,
          settlementPeriods: response.data.map(period => ({ ...period }))
        };
        resolvedSettlementKey.current = key;
        draftRef.current = readyDraft;
        setDraft(readyDraft);
      })
      .catch(() => {
        if (sequence !== settlementRequestSequence.current) return;
        const latest = draftRef.current;
        if (
          latest?.workType !== "selective" ||
          settlementKey(latest.startDate, latest.endDate) !== key
        ) {
          return;
        }
        const errorDraft = {
          ...latest,
          settlementStatus: "error" as const,
          settlementPeriods: []
        };
        draftRef.current = errorDraft;
        setDraft(errorDraft);
      });
  }, [api, draft, setDraft, settlementRetryNonce]);

  const loadStaggered = useCallback(async () => {
    if (!draft || draft.workType !== "staggered") return;
    setStaggeredStatus("loading");
    try {
      const response = await api.staggeredTimes(draft.workPolicyId);
      setStaggeredOptions(response.data);
      setStaggeredStatus("ready");
    } catch {
      setStaggeredStatus("error");
    }
  }, [api, draft]);

  useEffect(() => {
    void loadStaggered();
  }, [loadStaggered]);

  useEffect(() => {
    if (draft?.workType !== "staggered" || staggeredStatus !== "ready") return;
    const allowedStartTimes = staggeredOptions.map(option => option.startTime);
    if (
      draft.allowedStartTimes.length === allowedStartTimes.length &&
      draft.allowedStartTimes.every((time, index) => time === allowedStartTimes[index])
    ) {
      return;
    }
    controller.setDraft({ ...draft, allowedStartTimes });
  }, [controller, draft, staggeredOptions, staggeredStatus]);

  useEffect(() => {
    if (eligibleStatus !== "ready") return;
    const firstIssue = controller.validationIssues[0];
    if (!firstIssue) return;
    if (firstIssue.path.startsWith("schedule") && draft?.workType === "flexible") {
      const match = /^schedule\[(\d+)\](?:\.(.+))?$/.exec(firstIssue.path);
      const row = match ? draft.schedule[Number(match[1])] : undefined;
      const field = match?.[2];
      const rowLabel = row?.workDate ??
        (row?.weekdayNo
          ? ["월요일", "화요일", "수요일", "목요일", "금요일", "토요일", "일요일"][
              row.weekdayNo - 1
            ]
          : null);
      const suffix =
        field === "plannedWorkMinutes"
          ? "계획근로시간"
          : field === "startTime"
          ? "출근"
          : null;
      const labelledTarget =
        rowLabel && suffix
          ? Array.from(document.querySelectorAll<HTMLElement>("[aria-label]")).find(
              element => element.getAttribute("aria-label") === `${rowLabel} ${suffix}`
            )
          : undefined;
      const invalidTarget = document
        .getElementById("work-policy-schedule")
        ?.querySelector<HTMLElement>("[aria-invalid='true']");
      (labelledTarget ?? invalidTarget ?? document.getElementById("work-policy-schedule"))?.focus();
      return;
    }
    const targetId =
      firstIssue.path === "selectedStartTime"
        ? "work-policy-staggered-time"
        : `work-policy-${firstIssue.path}`;
    document.getElementById(targetId)?.focus();
  }, [controller.validationIssues, draft, eligibleStatus]);

  const selectedPolicy = useMemo(
    () => policies.find(policy => policy.workPolicyId === draft?.workPolicyId),
    [draft?.workPolicyId, policies]
  );

  const selectPolicy = useCallback(
    (policy: EligibleWorkPolicy) => {
      if (
        draft &&
        requiresWorkPolicySelectionConfirmation(draft, policy) &&
        !window.confirm(
          "정책을 변경하면 기존 유형별 상세 입력이 삭제됩니다. 변경할까요?"
        )
      ) {
        return;
      }
      controller.setValidationIssues([]);
      controller.setUnsupportedHistorical(null);
      settlementRequestSequence.current += 1;
      requestedSettlementKey.current = undefined;
      resolvedSettlementKey.current = undefined;
      const next = createWorkPolicyApplicationDraft(
        policy,
        startDate.value,
        endDate.value
      );
      if (endDate.value !== next.endDate) endDate.onChange(next.endDate);
      draftRef.current = next;
      controller.setDraft(next);
      controller.setIsDirty(true);
    },
    [controller, draft, endDate.value, startDate.value]
  );

  const updateDate = useCallback(
    (field: "startDate" | "endDate", value: string) => {
      if (!draft) {
        if (field === "startDate") startDate.onChange(value);
        else endDate.onChange(value);
        return;
      }
      const requested = { ...draft, [field]: value };
      const next = {
        ...requested,
        endDate:
          requested.startDate &&
          requested.endDate &&
          requested.endDate < requested.startDate
            ? requested.startDate
            : requested.endDate
      };
      if (
        next.workType === "flexible" &&
        next.flexibleMode === "DAILY_DATE"
      ) {
        next.endDate = clampCalendarRangeEnd(
          next.startDate,
          next.endDate,
          MAX_FLEXIBLE_DATE_RANGE_MONTHS
        );
      }
      if (
        next.workType === "flexible" &&
        next.flexibleMode === "DAILY_DATE" &&
        next.schedule.length > 0
      ) {
        if (
          !window.confirm(
            "신청기간을 변경하면 범위 밖 일정은 삭제되고 새 날짜가 추가됩니다. 변경할까요?"
          )
        ) {
          return;
        }
        const previousRows = new Map(
          next.schedule.map(row => [row.workDate, row] as const)
        );
        next.schedule = createWorkPolicyApplicationSchedule(
          next.flexibleMode,
          next.startDate,
          next.endDate
        ).map(row => previousRows.get(row.workDate) ?? row);
      }
      if (startDate.value !== next.startDate) startDate.onChange(next.startDate);
      if (endDate.value !== next.endDate) endDate.onChange(next.endDate);
      draftRef.current = next;
      controller.setDraft(next);
      controller.setIsDirty(true);
    },
    [controller, draft, endDate, startDate]
  );

  const updateStaggeredStartTime = useCallback(
    (selectedStartTime: string) => {
      if (draft?.workType !== "staggered") return;
      controller.setDraft({ ...draft, selectedStartTime });
      controller.setIsDirty(true);
    },
    [controller, draft]
  );

  const updateFlexibleSchedule = useCallback(
    (value: WorkPolicyApplicationScheduleRow[]) => {
      if (draft?.workType !== "flexible") return;
      controller.setDraft({ ...draft, schedule: value });
      controller.setIsDirty(true);
    },
    [controller, draft]
  );

  return {
    controller,
    eligibleStatus,
    policies,
    selectedPolicy,
    staggeredStatus,
    staggeredOptions,
    selectPolicy,
    updateDate,
    updateStaggeredStartTime,
    updateFlexibleSchedule,
    retryEligible: () => void loadEligible(),
    retryStaggered: () => void loadStaggered(),
    retrySettlement: () => {
      requestedSettlementKey.current = undefined;
      setSettlementRetryNonce(value => value + 1);
    }
  };
}
